import { Accordion, Text } from '@mantine/core';
import { IconChecklist, IconSeedling, IconTractor } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { IntercultureFormComplete } from '../../data-access/types/types';

import { FORM_STATUS, formatDate } from '@data-access';
import { CheckList, CheckListItem } from '@ui';

interface FormInfoProps {
  form: IntercultureFormComplete;
  withItemsIcon?: boolean;
}

export function FormInfo({ form, withItemsIcon = true }: FormInfoProps) {
  const { t } = useTranslation();

  const sewingList: CheckListItem[] = [
    {
      value: form.variety?.name,
      label: t('common.variety'),
    },
    {
      value: formatDate({
        format: 'display',
        date: form.seed_date,
      }),
      label: t('common.seed_date'),
    },
    {
      value: form.is_irrigated ? t('common.yes') : t('common.no'),
      label: t('common.is_irrigated'),
    },
    {
      value: formatDate({
        format: 'display',
        date: form.previous_crop_harvest_date,
      }),
      label: t('common.previous_crop_harvest_date'),
    },
    {
      value: form.previous_crop?.informed?.name,
      label: t('common.previous_crop'),
    },
  ];

  const harvestList: CheckListItem[] = [
    {
      value: form.is_harvested ? t('common.yes') : t('common.no'),
      label: t('common.is_harvested'),
    },
    {
      value: formatDate({
        format: 'display',
        date: form.harvest_date,
      }),
      label: t('common.harvest_date'),
    },
    {
      value: form.farm_yield_tons,
      label: t('common.yield_info_t'),
      unit: 't',
    },
    {
      value: form.informed_yield,
      label: t('common.yield_info_q'),
      unit: 'q/ha',
    },
  ];

  const resultsList: CheckListItem[] = [
    {
      value: form.interculture_crop?.estimated?.name,
      label: t('common.interculture_crop'),
      state:
        form.interculture_crop?.status === FORM_STATUS.UNDEFINED
          ? 'warning'
          : form.interculture_crop?.status === FORM_STATUS.FINISHED
            ? 'success'
            : 'error',
    },
    {
      value: form.previous_crop?.estimated?.name,
      label: t('common.previous_crop'),
      state:
        form.previous_crop?.status === FORM_STATUS.UNDEFINED
          ? 'warning'
          : form.previous_crop?.status === FORM_STATUS.FINISHED
            ? 'success'
            : 'error',
    },
    {
      value: form.yield_data?.estimated,
      label: t('common.yield_info_t'),
      unit: 't',
      state:
        form.yield_data?.status === FORM_STATUS.UNDEFINED
          ? 'warning'
          : form.yield_data?.status === FORM_STATUS.FINISHED
            ? 'success'
            : 'error',
    },
    {
      value: form.yield_data?.estimated_field,
      label: t('common.yield_info_q'),
      unit: 'q/ha',
      state:
        form.yield_data?.status === FORM_STATUS.UNDEFINED
          ? 'warning'
          : form.yield_data?.status === FORM_STATUS.FINISHED
            ? 'success'
            : 'error',
    },
    {
      value:
        form.status === FORM_STATUS.UNDEFINED
          ? t('common.indetermine')
          : form.status === FORM_STATUS.REJECTED
            ? t('common.refused')
            : t('common.accepted'),
      label: t('common.validation'),
      state:
        form.status === FORM_STATUS.UNDEFINED
          ? 'warning'
          : form.status === FORM_STATUS.FINISHED
            ? 'success'
            : 'error',
    },
  ];

  const accordion = [
    {
      title: t('interculture.sewing_data'),
      items: sewingList,
      icon: <IconSeedling />,
    },
    {
      title: t('interculture.harvest_data'),
      items: harvestList,
      icon: <IconTractor />,
    },
    {
      title: t('interculture.results'),
      items: resultsList,
      icon: <IconChecklist />,
    },
  ];
  return (
    <Accordion defaultValue={accordion.map(({ title }) => title)} multiple>
      {accordion.map(({ title, items, icon }) => (
        <Accordion.Item key={title} value={title}>
          <Accordion.Control icon={icon}>
            <Text fw="bold" size="lg">
              {title}
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <CheckList items={items} withIcon={withItemsIcon} />
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}

export default FormInfo;
